import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Button, Select, Checkbox, Typography, Divider, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useNotify } from '@/hooks';
import { API } from '@/services/api.service';
import { cn } from '@/libs/utils';

const { Option } = Select;
const { Title } = Typography;

/**
 * Statuary Documents Form Component (PEP, CROSO, PoA)
 *
 * Allows users to generate compliance documents by selecting:
 * - Document types (PEP HLB, CROSO HLB, PoA certificate HLB)
 * - Authorized admins (users with statuaryAuthorization flag)
 *
 * @param {string} companyId - Company ID
 * @param {Object} companyData - Company data including name
 */
export default function StatuaryDocsForm({
  companyId,
  companyData,
  // Profile: no card of its own and no title (the panel shows them).
  bare = false,
}) {
  const { t } = useTranslation(['forms', 'messages']);
  const notification = useNotify();
  const [form] = Form.useForm();

  const [allAuthorizedAdmins, setAllAuthorizedAdmins] = useState([]);
  const [checkedList, setCheckedList] = useState([]);
  const [checkAll, setCheckAll] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const options = [
    { label: 'PEP HLB', value: 'pep' },
    { label: 'CROSO HLB', value: 'croso' },
    { label: 'PoA certificate HLB', value: 'poa' },
  ];

  const fetchAllAuthorizedAdmins = async () => {
    try {
      const response = await API.users.retrieveDropdownAllUsers({
        filter: { statuaryAuthorization: true },
      });
      setAllAuthorizedAdmins(response.items || []);
    } catch (error) {
      console.error('Error fetching authorized admins:', error);
      notification.error({
        message: t('errorMessages.fetch_authorized_admins', { ns: 'messages' }),
        placement: 'bottomRight',
      });
    }
  };

  useEffect(() => {
    fetchAllAuthorizedAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onValuesChange = (_, allValues) => {
    const { authorizedAdmins } = allValues;
    setIsDisabled(
      checkedList.length === 0 || !authorizedAdmins || authorizedAdmins.length === 0
    );
  };

  const onFinish = async (values) => {
    setIsLoading(true);

    try {
      const response = await API.companies.createStatuaryDocuments({
        company: companyId,
        companyName: companyData?.name,
        statuaryType: values.statuaryType,
        authorizedAdmins: values.authorizedAdmins,
      });

      // Create blob and download ZIP file
      const blob = new Blob([response], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const fileName = `statuary_docs_${companyData?.name}.zip`;

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      notification.success({
        message: t('successMessages.documents_created', { ns: 'messages' }),
        placement: 'bottomRight',
      });

      setIsLoading(false);
    } catch (error) {
      console.error('Error creating statuary documents:', error);

      let errorMessage = t('errorMessages.documents_creation_failed', { ns: 'messages' });
      let errorDescription = null;

      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.fields && errorData.fields.length > 0) {
          errorMessage = errorData.error || t('errorMessages.documents_creation_failed', { ns: 'messages' });
          errorDescription = (
            <ul style={{ paddingLeft: '20px', margin: '8px 0 0 0' }}>
              {errorData.fields.map((field, index) => (
                <li key={index}>{field}</li>
              ))}
            </ul>
          );
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      }

      notification.error({
        message: errorMessage,
        description: errorDescription,
        placement: 'bottomRight',
        duration: 0,
      });

      setIsLoading(false);
    }
  };

  const onChange = (list) => {
    setCheckedList(list);
    setCheckAll(list.length === options.length);
    form.setFieldsValue({ statuaryType: list });

    // Update disabled state
    const authorizedAdmins = form.getFieldValue('authorizedAdmins');
    setIsDisabled(list.length === 0 || !authorizedAdmins || authorizedAdmins.length === 0);
  };

  const onCheckAllChange = (e) => {
    const newCheckedList = e.target.checked ? options.map((option) => option.value) : [];
    setCheckedList(newCheckedList);
    setCheckAll(e.target.checked);
    form.setFieldsValue({ statuaryType: newCheckedList });

    // Update disabled state
    const authorizedAdmins = form.getFieldValue('authorizedAdmins');
    setIsDisabled(
      newCheckedList.length === 0 || !authorizedAdmins || authorizedAdmins.length === 0
    );
  };

  return (
    <div
      className={cn(
        !bare && 'mx-5 my-5 max-w-3xl rounded-lg bg-white p-5 shadow-md'
      )}
    >
      {!bare && (
        <>
          <Title level={3} className="text-left">
            {t('statuary_documents.title', { ns: 'forms' })}
          </Title>
          <Divider style={{ backgroundColor: '#2db1ab50', margin: '20px 0' }} />
        </>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onValuesChange={onValuesChange}
      >
        <Form.Item
          name="statuaryType"
          label={t('statuary_documents.document_type', { ns: 'forms' })}
          rules={[
            {
              required: true,
              message: t('formValidation.required', { ns: 'forms' }),
            },
          ]}
        >
          <Checkbox.Group
            className="flex flex-col"
            value={checkedList}
            onChange={onChange}
          >
            {options.map((option) => (
              <Checkbox key={option.value} value={option.value} className="my-1">
                {option.label}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </Form.Item>

        <Form.Item>
          <div className="flex items-center">
            <Checkbox
              onChange={onCheckAllChange}
              checked={checkAll}
              indeterminate={checkedList.length > 0 && checkedList.length < options.length}
            >
              {t('statuary_documents.check_all', { ns: 'forms' })}
            </Checkbox>
          </div>
        </Form.Item>

        <Divider style={{ backgroundColor: '#2db1ab50', margin: '20px 0' }} />

        <Form.Item
          label={
            <div className="flex items-center">
              <span>{t('statuary_documents.grant_authorization', { ns: 'forms' })}</span>
              <Tooltip
                title={t('statuary_documents.authorization_tooltip', { ns: 'forms' })}
                color="#b1b1b1"
                placement="bottom"
                autoAdjustOverflow={true}
              >
                <QuestionCircleOutlined className="ml-2 cursor-pointer text-gray-400" />
              </Tooltip>
            </div>
          }
          name="authorizedAdmins"
          rules={[
            {
              required: true,
              message: t('formValidation.required', { ns: 'forms' }),
            },
          ]}
        >
          <Select
            mode="multiple"
            placeholder={t('statuary_documents.select_authorized_admins', { ns: 'forms' })}
          >
            {allAuthorizedAdmins?.map((item) => (
              <Option key={item._id} value={item._id}>
                {item.firstName} {item.lastName}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item className="mt-5">
          <Button
            loading={isLoading}
            htmlType={!isLoading ? 'submit' : 'button'}
            type="primary"
            block
            className="btn-primary"
            disabled={isDisabled}
          >
            {t('common.submit', { ns: 'forms' })}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}
